"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { getImageSrc } from "@/app/utils/getImageSrc";

const AUTOPLAY_MS = 4000; // time each milestone stays on screen

export const Timeline = ({ timeline }) => {
	const blocks = timeline.block;
	const last = blocks.length - 1;
	const [index, setIndex] = useState(0);
	const [dragX, setDragX] = useState(0); // live finger offset in px
	const [dragging, setDragging] = useState(false);
	const startX = useRef(null);
	const viewportRef = useRef(null);
	const sectionRef = useRef(null);
	const [playing, setPlaying] = useState(true); // user play/pause button
	const [hovered, setHovered] = useState(false);
	const [inView, setInView] = useState(true);
	const [tabVisible, setTabVisible] = useState(true);
	const [reduceMotion, setReduceMotion] = useState(false);

	const go = (n) => setIndex(Math.max(0, Math.min(last, n)));

	// autoplay: advance, loop back to the first milestone after the last
	useEffect(() => {
		if (!playing || hovered || dragging || !inView || !tabVisible || reduceMotion) return;
		const t = setTimeout(() => setIndex((i) => (i >= last ? 0 : i + 1)), AUTOPLAY_MS);
		return () => clearTimeout(t); // restarts after every slide change, manual or automatic
	}, [index, playing, hovered, dragging, inView, tabVisible, reduceMotion, last]);

	// pause when the section is off-screen or the browser tab is hidden
	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
		setReduceMotion(mq.matches);
		const onVis = () => setTabVisible(!document.hidden);
		document.addEventListener("visibilitychange", onVis);
		const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
		if (sectionRef.current) io.observe(sectionRef.current);
		return () => {
			document.removeEventListener("visibilitychange", onVis);
			io.disconnect();
		};
	}, []);

	// year rail: each year once, jumps to its first milestone
	const uniqueYears = blocks.reduce((acc, block, i) => {
		if (!acc.some((item) => item.year === block.year)) {
			acc.push({ year: block.year, firstIndex: i });
		}
		return acc;
	}, []);
	const activeYear = blocks[index].year;

	const onKeyDown = (e) => {
		if (e.key === "ArrowLeft") go(index - 1);
		if (e.key === "ArrowRight") go(index + 1);
	};

	// swipe: the track follows the finger, then snaps smoothly
	const onTouchStart = (e) => {
		startX.current = e.touches[0].clientX;
		setDragging(true);
	};
	const onTouchMove = (e) => {
		if (startX.current === null) return;
		let dx = e.touches[0].clientX - startX.current;
		// resistance at the first / last slide
		if ((index === 0 && dx > 0) || (index === last && dx < 0)) dx *= 0.3;
		setDragX(dx);
	};
	const onTouchEnd = () => {
		const width = viewportRef.current?.offsetWidth || 1;
		if (Math.abs(dragX) > Math.min(80, width * 0.2)) go(dragX < 0 ? index + 1 : index - 1);
		setDragX(0);
		setDragging(false);
		startX.current = null;
	};

	const arrow =
		"flex-none w-11 h-11 md:w-12 md:h-12 rounded-full border border-white text-white text-2xl leading-none flex items-center justify-center transition-all duration-300 hover:bg-white hover:text-[#152F86] hover:scale-110 active:scale-95 disabled:opacity-30 disabled:pointer-events-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

	return (
		<section
			className="hp-timeline p-0 border-b-2 border-[#162F86]"
			aria-roledescription="carousel"
			aria-label="Journey and Milestones"
			ref={sectionRef}
			onKeyDown={onKeyDown}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onFocus={() => setHovered(true)}
			onBlur={() => setHovered(false)}
		> 

			<div className="py-10 lg:py-16 bg-primary relative timeline">
				<div className="max-w-[90%] lg:max-w-[70%] mx-auto">
					
					{/* year rail */}
					<div className="milestone_years flex gap-2 overflow-x-auto" role="tablist" aria-label="Milestone years">
						{uniqueYears.map((item) => {
							const active = activeYear === item.year;
							return (
								<button
									key={item.firstIndex}
									type="button"
									role="tab"
									aria-selected={active}
									onClick={() => go(item.firstIndex)}
									className={`flex-none min-h-[44px] px-4 text-white border-b-[3px] transition-all duration-300 ${
										active ? "border-white font-bold" : "border-transparent opacity-70 hover:opacity-100"
									}`}
								>
									{item.year}
								</button>
							);
						})}
					</div>

					<div className="milestone_slider flex items-center gap-3 md:gap-5">
						<button type="button" className={`${arrow} hidden md:flex`} onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous milestone">
							‹
						</button>

						<div
							ref={viewportRef}
							className="flex-1 overflow-hidden touch-pan-y"
							onTouchStart={onTouchStart}
							onTouchMove={onTouchMove}
							onTouchEnd={onTouchEnd}
							aria-live={playing && !hovered ? "off" : "polite"}
						>
							<div
								className={`tl-track ${dragging ? "is-dragging" : ""}`}
								style={{ transform: `translateX(calc(${-index * 100}% + ${dragX}px))` }}
							>
								{blocks.map((block, i) => (
									<div
										key={i}
										className={`tl-slide ${i === index ? "is-active" : ""}`}
										aria-hidden={i !== index}
									>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
											<div className="bg-[#152F86] p-4 lg:p-6">
												<Image
													alt={block.image?.alt || block.title || "Hindustan Power milestone"}
													src={getImageSrc(block.image.url)}
													width={100}
													height={80}
													unoptimized
													loading={Math.abs(i - index) <= 1 ? "eager" : "lazy"}
													className="w-full h-auto"
												/>
											</div>

											<div className="text-white tl-copy">
												<div className="temp104years">{block.year}</div>
												<div className="temp104Copy">
													<h2>{block.title}</h2>
													{block.content && (
														<ul>
															<li>{block.content}</li>
														</ul>
													)}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>

						<button type="button" className={`${arrow} hidden md:flex`} onClick={() => go(index + 1)} disabled={index === last} aria-label="Next milestone">
							›
						</button>
					</div>

					{/* progress bar + counter */}
					<div className="mt-6 flex items-center gap-4">
						<div className="flex-1 h-[3px] bg-white/25 overflow-hidden">
							<div className="tl-bar h-full bg-white" style={{ width: `${((index + 1) / blocks.length) * 100}%` }} />
						</div>
						<div className="text-white text-sm opacity-80 tabular-nums">
							{index + 1} / {blocks.length}
						</div>
						{!reduceMotion && (
							<button
								type="button"
								onClick={() => setPlaying((p) => !p)}
								aria-label={playing ? "Pause autoplay" : "Start autoplay"}
								className="flex-none w-9 h-9 rounded-full border border-white text-white flex items-center justify-center transition-colors hover:bg-white hover:text-[#152F86] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
							>
								{playing ? (
									<svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="1" y="1" width="3.5" height="10" /><rect x="7.5" y="1" width="3.5" height="10" /></svg>
								) : (
									<svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M2 1l9 5-9 5z" /></svg>
								)}
							</button>
						)}
					</div>

					{/* mobile arrows */}
					<div className="flex md:hidden justify-center gap-4 mt-6">
						<button type="button" className={arrow} onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous milestone">
							‹
						</button>
						<button type="button" className={arrow} onClick={() => go(index + 1)} disabled={index === last} aria-label="Next milestone">
							›
						</button>
					</div>

				</div>
			</div>
		</section>
	);
};